<script setup lang="ts">
import type { SubmitEventPromise } from 'vuetify';
import type {
  InitialData as DriverReportGenerateDialogFormInitialData,
  ValidData as DriverReportGenerateDialogFormValidData,
} from '@/components/dialogForms/DriverReportGenerateDialogForm.vue';

import { computed, useTemplateRef } from 'vue';

import { getCurrentWeekDates } from '@/utils/date';
import ReportGenerateDialogAlert from '@/components/alerts/ReportGenerateDialogAlert.vue';
import KBtn from '@/components/btns/KBtn.vue';
import DriverReportGenerateDialogForm from '@/components/dialogForms/DriverReportGenerateDialogForm.vue';
import ReportGenerateDialogHeader from '@/components/headers/ReportGenerateDialogHeader.vue';
import PageDialogWrapper from '@/components/wrappers/PageDialogWrapper.vue';
import useCurrentDateString from '@/composables/useCurrentDateString';

export interface Props {
  isConfirmLoading?: boolean;
  confirm?: (
    formData: DriverReportGenerateDialogFormValidData,
  ) => Promise<unknown>;
}
export type DefProps = typeof props;
const props = withDefaults(defineProps<Props>(), {
  isConfirmLoading: false,
  confirm: undefined,
});
const emit = defineEmits<{
  cancelBtnClick: [event: Event];
  confirmBtnClick: [
    event: SubmitEventPromise | null | undefined,
    formData: DriverReportGenerateDialogFormValidData,
    props: DefProps,
  ];
}>();

function handleCancelClick(event: Event): void {
  // also it should be wrapped with <v-dialog :persistent="isConfirmLoading"
  if (props.isConfirmLoading) return;
  emit('cancelBtnClick', event);
}

function handleSubmit(
  event: SubmitEventPromise | null | undefined,
  formData: DriverReportGenerateDialogFormValidData,
): void {
  if (props.isConfirmLoading) return;
  emit('confirmBtnClick', event, formData, props);
}

const mainEl = useTemplateRef('main');

const { getCurrentDateString } = useCurrentDateString();

function getSubmitBtn(): HTMLInputElement | null {
  if (!mainEl.value) return null;
  return mainEl.value.querySelector('input[type=submit]');
}

function handleConfirmClick(event: Event): void {
  const submitBtn = getSubmitBtn();
  if (!submitBtn) return;
  submitBtn.click();
}

const initialData = computed<DriverReportGenerateDialogFormInitialData>(() => {
  return {
    monthDate: getCurrentDateString(),
    weekRange: getCurrentWeekDates(),
  };
});
</script>

<template>
  <PageDialogWrapper
    :class="$style.driverReportGenerateDialog"
    data-testid="driverReportGenerateDialog"
  >
    <template #header>
      <ReportGenerateDialogHeader @close-click="handleCancelClick">
        New Driver Report
      </ReportGenerateDialogHeader>
    </template>
    <section ref="main" :class="$style.main">
      <DriverReportGenerateDialogForm
        :initial-data="initialData"
        :loading="props.isConfirmLoading"
        @submit="handleSubmit"
      />
      <ReportGenerateDialogAlert
        icon="mdi-alert-circle-outline"
        :class="$style.alert"
      >
        The report will be available in about 10&nbsp;min in the related table
      </ReportGenerateDialogAlert>
    </section>
    <template #footer>
      <menu ref="footerMenu" :class="$style.dialogFooterMenuList">
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            variant="outlined"
            append-icon="mdi-close"
            :class="$style.btn"
            @click="handleCancelClick"
          >
            Cancel
          </KBtn>
        </li>
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            append-icon="mdi-plus"
            :class="$style.btn"
            :loading="props.isConfirmLoading"
            :disabled="!props.confirm"
            @click="handleConfirmClick"
          >
            Generate
          </KBtn>
        </li>
      </menu>
    </template>
  </PageDialogWrapper>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.driverReportGenerateDialog {
  width: variables.$reportGenerateDialogWidth;
}
  .main {
  }
  .alert {
    margin-top: 15px;
  }
  .dialogFooterMenuList {
    list-style: none;
    margin: 0;
    padding: 0;
    text-align: right;
  }
    .dialogFooterMenuListItem {
      display: inline;
    }
    .dialogFooterMenuListItem + .dialogFooterMenuListItem {
      margin-left: 1rem;
    }
      .btn {
      }
</style>
